import { routeStopIssues } from '@/helpers/address.js'

/**
 * Ecran d'edition d'une operation, par categorie (`operationcategories`).
 *
 * Ne PAS retomber sur /sale/:id par defaut : cette vue redirige vers
 * /pages/error-404 des que la categorie n'est pas 2, donc le lien « va corriger
 * l'adresse ici » envoyait sur une page d'erreur pour une reception ou un bon
 * de commande — exactement les deux cas encore incomplets en base.
 */
const CATEGORY_ROUTES = {
  1: { path: '/reception/', label: 'reception' },
  2: { path: '/invoice/', label: 'invoice' },
  5: { path: '/quotation/', label: 'quotation' },
  6: { path: '/purchase/', label: 'purchase order' }
}

/**
 * Garde commune aux écrans qui impriment un document par arrêt de transport
 * (CMR, aviz, réception) à partir de `routetransport`.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI CE MIXIN EXISTE
 * ---------------------------------------------------------------------------
 * Le CMR du transport 234255 ne se téléchargeait pas : la console affichait
 * `TypeError: can't access property "split", routetransport[i].addresses is
 * null` et rien d'autre. La vente était bloquée sans que personne ne sache
 * pourquoi ni quoi corriger.
 *
 * Cause : l'endpoint construisait la colonne `addresses` par concaténation SQL,
 * et `'x' || NULL = NULL` en Postgres — un client dont l'adresse n'était plus
 * reliée dans `people_addresses` annulait toute la ligne. 592 transports
 * étaient dans cet état. Le backend a été réécrit (replis en cascade), mais
 * une donnée peut rester réellement absente : dans ce cas on refuse d'imprimer
 * un document incomplet, et on dit lequel des arrêts est en cause et où aller
 * le corriger.
 *
 * Le garde des boutons testait `routetransport[0].addresses` : un transport
 * dont seul le PREMIER arrêt était complet ouvrait quand même le générateur,
 * qui plantait à l'arrêt suivant. Ici, tous les arrêts sont vérifiés.
 *
 * Prérequis : le composant expose `routetransport` (réponse de
 * /getroutetransportv2), dont les lignes portent `aviz_number`, `id` (facture)
 * et `idoperation` (devis).
 */
export default {
  computed: {
    /** Arrêts inexploitables, avec le lien vers l'opération à corriger. */
    incompleteStops () {
      return (this.routetransport || [])
        .map((row, index) => {
          const issues = routeStopIssues(row)
          if (!issues.length) return null
          // La facture porte l'adresse de livraison des qu'elle existe : c'est
          // elle qu'il faut corriger. Sinon on retombe sur le devis, dont la
          // categorie decide de l'ecran.
          const target = Boolean(row.id)
            ? { id: row.id, route: CATEGORY_ROUTES[2] }
            : { id: row.idoperation, route: CATEGORY_ROUTES[Number(row.iddeviscategory)] }
          return {
            key: row.idoperation || row.id || index,
            aviz: row.aviz_number || '?',
            issues: issues.join(' and '),
            // Categorie inconnue (ancienne API, categorie non editable) : pas de
            // lien plutot qu'un lien vers une 404.
            link: target.route && target.id ? target.route.path + target.id : null,
            linkLabel: target.route && target.id ? target.route.label + ' ' + target.id : null
          }
        })
        .filter(Boolean)
    },
    documentsBlocked () {
      return this.incompleteStops.length > 0
    },
    /**
     * POURQUOI le bouton document est rouge, en clair.
     *
     * Les boutons portaient un libellé FIXE — « (Please generate Final Invoice
     * with address) » — affiché quelle que soit la cause. Le 2026-08-17 la cause
     * était un HTTP 500 sur /getroutetransportv2 ; le bouton accusait l'adresse
     * de livraison et l'équipe a cherché deux heures du mauvais côté.
     * Le garde a trois conditions : on dit laquelle a échoué.
     */
    documentBlockReason () {
      if (!this.user_data || !this.user_data.products || this.user_data.products.length === 0) {
        return 'no product line on this transport'
      }
      // dataLoadFailed vient du mixin documentDataLoad, absent de certains
      // écrans : on ne le lit que s'il existe.
      if (this.dataLoadFailed) return 'data not loaded from the server - see the red banner'
      if (!this.routetransport || this.routetransport.length === 0) {
        return 'no stop attached to this transport'
      }
      if (this.documentsBlocked) return 'incomplete delivery data - see the red banner'
      return ''
    }
  },
  methods: {
    /**
     * À appeler en tête de chaque générateur PDF : renvoie `true` (et explique)
     * quand le document ne peut pas être produit.
     */
    warnDocumentBlocked () {
      if (!this.documentsBlocked) return false
      const stops = this.incompleteStops
      const text =
        'Documents cannot be generated. ' +
        stops.map((stop) => 'Aviz ' + stop.aviz + ': missing ' + stop.issues + '.').join(' ') +
        ' Set the delivery address on the linked operation, then reload this page.'
      // `$vs.dialog` n'imprime que du texte brut (pas de v-html) : le lien
      // cliquable est porté par le bouton d'action, et l'alerte de la page
      // liste tous les arrêts fautifs avec leurs liens.
      const first = stops.find((stop) => stop.link)
      if (!first) {
        this.$vs.dialog({ color: 'danger', title: 'Incomplete delivery data', text, acceptText: 'Close' })
        return true
      }
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Incomplete delivery data',
        text,
        acceptText: 'Open ' + first.linkLabel,
        cancelText: 'Close',
        accept: () => {
          this.$router.push(first.link).catch(() => { })
        }
      })
      return true
    }
  }
}
